import type { Models_Forms_FormField, RequestModels_Forms_V2_FormsRequestModelV2 } from '@monorepo-types/dc';

import type { FormValues } from './types';
import type { CorrectionInvoiceFormContent } from '../correction-invoice-form-content.types';

/**
 * Mirrors `libs/sitecore/components/oxxio/src/CorrectionInvoiceForm/util/prepareRequestBody.ts`: each
 * answer keyed by its field's label, the meter readings by their meter label plus the field label.
 */
export const prepareRequestBody = (
  values: FormValues,
  content: CorrectionInvoiceFormContent,
  destinationCode: string,
) => {
  const {
    initials,
    surnamePreposition,
    surname,
    postalCode,
    houseNumberSuffix,
    street,
    city,
    emailAddress,
    changeType,
  } = values;

  // Sitecore always sends a label, `''` when empty; the Sanity migration drops empty strings. Defaulting
  // to `''` keeps each key exactly as Sitecore would build it.
  const label = (field?: { label?: string }) => field?.label ?? '';
  const meterKey = (meterLabel?: string, fieldLabel?: string) => `${meterLabel ?? ''} ${fieldLabel ?? ''}`;

  const personalValues = [
    { key: label(content.customerIdFormField), value: values.customerId },
    { key: label(content.salutationFormField), value: values.salutation },
    { key: label(content.firstNameFormField), value: values.firstName },
    { key: label(content.phoneNumberFormField), value: values.phoneNumber },
  ];

  const invoiceValues = [
    { key: label(content.invoiceNumberFormField), value: values.invoiceId },
    { key: label(content.invoiceDateFormField), value: values.invoiceDate },
  ];

  const correctionRequestValues = [
    {
      key: label(content.yearnoteIssueFormField),
      value: values.correctionReason === 'other' ? values.otherCorrectionReason : values.correctionReason,
    },
    {
      key: label(content.correctionMeterReadingsFormField),
      value: values.changeType,
    },
  ];

  const meterReadingValues: Models_Forms_FormField[] = [];
  if (changeType !== 'end_result') {
    meterReadingValues.push(
      { key: label(content.startDateFormField), value: values.readingStartDate },
      {
        key: meterKey(content.electricityLowLabel, content.electricityLowStartPositionFormField?.label),
        value: values.electricityLowStart,
      },
      {
        key: meterKey(
          content.electricityNormalSingleMeterLabel,
          content.electricityNormalSingleMeterStartPositionFormField?.label,
        ),
        value: values.electricityNormalStart,
      },
      {
        key: meterKey(content.redeliveryLowLabel, content.redeliveryLowStartPositionFormField?.label),
        value: values.redeliveryLowStart,
      },
      {
        key: meterKey(
          content.redeliveryNormalSingleMeterLabel,
          content.redeliveryNormalSingleMeterStartPositionFormField?.label,
        ),
        value: values.redeliveryNormalStart,
      },
      { key: meterKey(content.gasLabel, content.gasStartPositionFormField?.label), value: values.gasStart },
    );
  }
  if (changeType !== 'start_position') {
    meterReadingValues.push(
      { key: label(content.endDateFormField), value: values.readingEndDate },
      {
        key: meterKey(content.electricityLowLabel, content.electricityLowEndResultFormField?.label),
        value: values.electricityLowEnd,
      },
      {
        key: meterKey(
          content.electricityNormalSingleMeterLabel,
          content.electricityNormalSingleMeterEndResultFormField?.label,
        ),
        value: values.electricityNormalEnd,
      },
      {
        key: meterKey(content.redeliveryLowLabel, content.redeliveryLowEndResultFormField?.label),
        value: values.redeliveryLowEnd,
      },
      {
        key: meterKey(
          content.redeliveryNormalSingleMeterLabel,
          content.redeliveryNormalSingleMeterEndResultFormField?.label,
        ),
        value: values.redeliveryNormalEnd,
      },
      { key: meterKey(content.gasLabel, content.gasEndResultFormField?.label), value: values.gasEnd },
    );
  }

  const requestBody: RequestModels_Forms_V2_FormsRequestModelV2 = {
    data: {
      destinationCode,
      urlOrigin: window.location.href,
      attachments: [],
      initials,
      surnamePreposition,
      surname,
      postalCode,
      houseNumber: +values.houseNumber,
      houseNumberSuffix,
      street,
      city,
      emailAddress,
      formFields: [...personalValues, ...invoiceValues, ...correctionRequestValues, ...meterReadingValues],
    },
  };
  return requestBody;
};
